import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { User, AuthSession } from '../types'
import { API_BASE } from '../utils/api'
const SESSION_KEY = 'lamoney-session'
const TOKEN_KEY = 'lamoney-token'

interface AuthContextType {
  session: AuthSession | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  isAdmin: boolean
  users: User[]
  token: string | null
  loading: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem(SESSION_KEY)
    const savedToken = localStorage.getItem(TOKEN_KEY)
    if (savedSession && savedToken) {
      try {
        const s = JSON.parse(savedSession) as AuthSession
        if (s.expiresAt > Date.now()) {
          setSession(s)
          setToken(savedToken)
        } else {
          // Session expired — clean up
          localStorage.removeItem(SESSION_KEY)
          localStorage.removeItem(TOKEN_KEY)
        }
      } catch {
        localStorage.removeItem(SESSION_KEY)
        localStorage.removeItem(TOKEN_KEY)
      }
    }
    setLoading(false)
  }, [])

  // Fetch users list for admin (only when logged in as admin)
  useEffect(() => {
    if (!session || session.user.role !== 'admin' || !token) return
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          setUsers(data.map((u: any) => ({
            id: u.id,
            username: u.username,
            displayName: u.display_name || u.username,
            role: u.role,
            status: u.status,
            email: u.email,
          })))
        }
      } catch {
        // Fallback — users will be empty
      }
    }
    fetchUsers()
  }, [session, token])

  // Token refresh — refresh 30 minutes before expiry
  useEffect(() => {
    if (!session || !token) return
    const timeToExpiry = session.expiresAt - Date.now()
    const refreshAt = timeToExpiry - 30 * 60 * 1000 // 30 min before expiry
    if (refreshAt <= 0) return

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json()
          const newSession: AuthSession = {
            ...session,
            token: data.token,
            expiresAt: Date.now() + data.expiresIn * 1000,
          }
          setSession(newSession)
          setToken(data.token)
          localStorage.setItem(SESSION_KEY, JSON.stringify(newSession))
          localStorage.setItem(TOKEN_KEY, data.token)
        }
      } catch {
        // Refresh failed — user will need to re-login when token expires
      }
    }, refreshAt)

    return () => clearTimeout(timer)
  }, [session, token])

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Login failed' }))
        console.warn('Login failed:', err.error)
        return false
      }

      const data = await res.json()
      const authSession: AuthSession = {
        user: {
          id: data.user.id,
          username: data.user.username,
          role: data.user.role,
          displayName: data.user.displayName,
          status: 'active',
        },
        token: data.token,
        expiresAt: Date.now() + data.expiresIn * 1000,
        mappedBroker: data.mappedBroker || null,
      }

      setSession(authSession)
      setToken(data.token)
      localStorage.setItem(SESSION_KEY, JSON.stringify(authSession))
      localStorage.setItem(TOKEN_KEY, data.token)
      return true
    } catch (err) {
      console.error('Auth API unavailable:', err)
      return false
    }
  }, [])

  const logout = useCallback(async () => {
    // Call logout API to blacklist token
    if (token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        })
      } catch {
        // Logout API failure is non-critical — still clear local state
      }
    }
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(TOKEN_KEY)
    setSession(null)
    setToken(null)
    setUsers([])
  }, [token])

  return (
    <AuthContext.Provider value={{
      session,
      login,
      logout,
      isAdmin: session?.user.role === 'admin',
      users,
      token,
      loading,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}
